import { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import type { ReactNode, MouseEvent as ReactMouseEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { EditorDocument, SectionEntry } from 'entities/document';
import { Editable, Slate, ReactEditor, RenderLeafProps } from 'slate-react';
import { Node, Element as SlateElement, Editor as SlateEditor, Descendant, NodeEntry, Range, Ancestor } from 'slate';
import root from 'react-shadow';
import { useDocument } from 'app/api/documents/useDocument';
import { useEditor } from './useEditor';
import { CustomEditor } from 'slate/types/slate';
import { EditorContext } from 'app/state/contexts/EditorContext';
import { EditorScopeProvider } from 'app/state/contexts/EditorScopeContext';
import { EditorSectionProvider, EditorUiProvider, useEditorUiSetters, EditorLiveSection } from 'app/state/contexts/EditorUiContext';
import useSelection from './useSelection';
import editorCss from './editor-style';
import useSave from './useSave';
import { useLocation } from '@tanstack/react-router';
import { useEditorSearch, useIsViewingVersion } from 'app/router/params';
import { useCurrentUser } from 'entities/user';
import { useComments } from 'entities/comment';
import { findCitationWithContext } from 'slate/utils';
import { findTagDomNode, scrollToNode, blinkTagNode } from 'app/utils/tag-utils';
import { composeDecorators } from 'slate/decorators/composeDecorators';
import { CoverageScopeProvider } from 'app/components/editor/coverage/CoverageScopeContext';

export type DecoratorFactory = (editor: CustomEditor) => (entry: NodeEntry) => Range[];

type EditorCss = NonNullable<ReturnType<typeof useDocument>['css']>;

interface CurrentUserInfo {
  userProperties?: { spellCheck?: boolean };
}

interface EditorInstance {
  documentId: number | string;
  documentRevisionId?: string;
  sectionId?: string;
  sectionRevisionId?: string;
  translationId?: string;
}

interface SingleEditorProps {
  autoSave?: boolean;
  children?: ReactNode;
  extensions: Parameters<typeof useEditor>[0];
  decorators?: DecoratorFactory[];
  instance: EditorInstance;
  readOnly?: boolean;
  sectionHeader?: boolean;
  showDiff?: boolean;
  withShadowDom?: boolean;
  showErrors?: boolean;
  usingStaticStructure?: Descendant[];
  useDraft?: boolean;
  withStyle?: unknown;
  forcePaperFormat?: boolean;
  useReferenceData?: boolean;
  pageBorders?: boolean;
  showFormatting?: boolean;
}

const useSearchQuery = () => {
  return new URLSearchParams(useLocation().searchStr);
};

/**
 * Multiple editors can be hosted in the same window: each one's state is scoped by its position
 * in the tree (EditorScopeProvider / EditorUiProvider), derived from its `instance` prop. No id
 * is passed or needed
 */
export const Editor = (props: SingleEditorProps) => {
  const { children, ...rest } = props;

  // The UI provider wraps SingleEditor rather than living inside it: SingleEditor's own body
  // (handleChange, useSave, useSelection) calls the setters, so it must be a consumer, not the
  // provider — otherwise those calls silently hit the no-op defaults.
  return (
    <EditorUiProvider>
      <SingleEditor {...rest}>{children}</SingleEditor>
    </EditorUiProvider>
  );
};

export const SingleEditor = (props: SingleEditorProps) => {
  const {
    autoSave,
    children: toolbars,
    extensions,
    decorators,
    instance: { documentId, documentRevisionId, sectionId, sectionRevisionId, translationId },
    readOnly,
    sectionHeader,
    showDiff,
    withShadowDom = true,
    showErrors = true,
    usingStaticStructure,
    useDraft = false,
    withStyle,
    forcePaperFormat = false,
    useReferenceData = true,
    pageBorders = false,
    showFormatting,
  } = props;

  const lazyCss = useRef<EditorCss>();
  const initialContent = useRef<Descendant[] | null>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const lastActiveSectionKey = useRef<string | undefined>(undefined);
  const lastActiveNodeKey = useRef<string>('');
  const pendingConnection = useRef(new Set<string>());
  const [shadowHost, setShadowHost] = useState<HTMLElement | null>(null);
  const editor = useEditor(extensions);
  const shouldUseDraft = useSearchQuery().get('useDraft') || useDraft;
  const embedd = useSearchQuery().get('embedd') || false;
  useSelection(editor, shadowHost);
  const decorate = composeDecorators(editor, ...(decorators || []));
  const renderLeaf = useCallback(
    (props: RenderLeafProps) => {
      editor.showFormatting = showFormatting;
      return editor.renderLeaf(props);
    },
    [editor, showFormatting]
  );
  const { comments, deleteCommentConnection, restoreCommentConnection } = useComments(sectionId as string);
  const {
    document,
    section: sectionData,
    css,
    addTemplateToDocument,
    scope,
  } = useDocument(
    documentId,
    documentRevisionId,
    sectionId,
    sectionRevisionId,
    translationId,
    showErrors,
    !!shouldUseDraft,
    usingStaticStructure,
    withStyle,
    useReferenceData,
    !!embedd
  );
  const section = sectionData as SectionEntry | null;
  if (css) {
    lazyCss.current = css;
  }
  const { setActiveSection, setActiveNode } = useEditorUiSetters();
  const isViewingVersion = useIsViewingVersion();
  const [liveSection, setLiveSection] = useState<SectionEntry | null>(null);
  useEffect(() => {
    if (section) setLiveSection(section);
  }, [section]);
  const [saveSection, adoptSectionRevision] = useSave(
    (document || {}) as EditorDocument,
    (section || {}) as SectionEntry,
    sectionRevisionId || null,
    translationId as string | null,
    setLiveSection
  );
  const location = useLocation();
  const { goToTagId, goToTagOnId, goToCommentId } = useEditorSearch();
  const handledTagJump = useRef(false);
  const { user: currentUser } = (useCurrentUser()?.data || {}) as { user?: CurrentUserInfo };

  editor.onInsertTemplate = addTemplateToDocument;
  editor.adoptSectionRevision = adoptSectionRevision;
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && event.shiftKey) {
      //Soft break on shift enter
      event.preventDefault();
      editor.insertText('\n');
    }
  };

  const onEditableClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    try {
      ReactEditor.toSlateNode(editor, event.target as HTMLElement);
      return false;
    } catch {
      return true;
    }
  };

  const handleChange = (newValue: Descendant[]) => {
    if (editor.selection) {
      editor.lastSelection = editor.selection;
    }

    if (!!editor.selection && !!editor.selection.anchor) {
      const activeSection = editor.children[editor.selection.anchor.path[0]];
      const activeNode = SlateEditor.parent(editor, editor.selection.anchor.path)[0];
      const sectionKey = SlateElement.isElement(activeSection) ? activeSection.uuid : undefined;
      if (sectionKey !== lastActiveSectionKey.current) {
        lastActiveSectionKey.current = sectionKey;
        setActiveSection(SlateElement.isElement(activeSection) ? activeSection : {});
      }
      const nodeKey = activeNode ? JSON.stringify({ ...activeNode, children: undefined }) : '';
      if (nodeKey !== lastActiveNodeKey.current) {
        lastActiveNodeKey.current = nodeKey;
        setActiveNode(activeNode);
      }
    }

    const hasMutation = editor.operations.some((op) => op.type !== 'set_selection');
    if (hasMutation) {
      if (autoSave && !isViewingVersion) {
        saveSection(newValue, editor);
      }
      if (section) {
        setLiveSection({ ...section, content: newValue });
      }
    }

    const anchor = editor.selection?.anchor;
    if (!anchor || isViewingVersion) return;
    try {
      const [parentNode] = SlateEditor.parent(editor, anchor.path);

      comments
        ?.filter((c) => c.onId === (SlateElement.isElement(parentNode) ? parentNode.uuid : undefined))
        .forEach((comment) => {
          const occurences = findCitationWithContext(Node.string(parentNode), comment.citation);
          const isDisconnected = !!comment.citationDeletedAt;
          const shouldDisconnect = occurences.length === 0 && !isDisconnected;
          const shouldReconnect = occurences.length > 0 && isDisconnected;
          if ((!shouldDisconnect && !shouldReconnect) || pendingConnection.current.has(comment.id)) {
            return;
          }
          pendingConnection.current.add(comment.id);
          const mutate = shouldDisconnect ? deleteCommentConnection : restoreCommentConnection;
          mutate(comment.id, { onSettled: () => pendingConnection.current.delete(comment.id) });
        });
    } catch (error) {
      console.error('Error during delayed deleteBackward override:', error);
    }
  };

  useEffect(() => {
    if (!shadowHost) return;
    const yOffset = -117; // Offset for the sub toolbar
    const elementId = location.hash || undefined;
    const pageNode = findPageNode(editor, elementId);
    lastActiveSectionKey.current = SlateElement.isElement(pageNode) ? pageNode.uuid : undefined;
    setActiveSection(SlateElement.isElement(pageNode) ? pageNode : {});

    const shadowRoot = shadowHost.shadowRoot;

    const scrollToPage = () => {
      const element = shadowRoot?.querySelector(`#id-${elementId}`);
      const y = (element?.getBoundingClientRect().top ?? NaN) + window.pageYOffset + yOffset;
      if (y) {
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    };

    if (goToTagId && !handledTagJump.current) {
      handledTagJump.current = true;
      let frames = 0;
      const resolveTag = () => {
        const node = shadowRoot ? findTagDomNode(shadowRoot, { id: goToTagId, onId: goToTagOnId }) : null;
        if (node) {
          scrollToNode(node);
          blinkTagNode(node);
        } else if (frames++ < 10) {
          requestAnimationFrame(resolveTag);
        } else {
          scrollToPage();
        }
      };
      resolveTag();
      return;
    }

    if (goToCommentId) return;

    scrollToPage();
  }, [location.state.key, location.hash, goToTagId, goToTagOnId, goToCommentId, shadowHost, editor, setActiveSection]);

  const content = section?.content as Descendant[] | undefined;
  if (!document || !content || content.length === 0) {
    return null;
  }
  if (initialContent.current === null) {
    initialContent.current = window.structuredClone(content);
  }
  const sectionName = document?.content.sections.find((s: SectionEntry) => s.superId === sectionId)?.name;
  return (
    <EditorScopeProvider scope={scope}>
      <EditorSectionProvider section={(liveSection ?? section) as EditorLiveSection | null}>
        <div ref={editorContainerRef} className="editor-container">
          <EditorContext.Provider value={{ editorContainerRef, shadowHost, documentRevisionId: document?.revisionId }}>
            <Slate editor={editor} initialValue={initialContent.current ?? []} onChange={handleChange}>
              <CoverageScopeProvider>
                <style type="text/css">{lazyCss.current?.exposedCss}</style>
                <Shadowed withShadowDom={withShadowDom} className="editor-shadow-host" ref={setShadowHost}>
                  <Editable
                    readOnly={readOnly}
                    renderElement={editor.renderElement}
                    decorate={decorate}
                    onClick={onEditableClick}
                    onKeyDown={onKeyDown}
                    renderLeaf={renderLeaf}
                    className="editor"
                    spellCheck={currentUser?.userProperties?.spellCheck || false}
                    scrollSelectionIntoView={handleScrollIntoView}
                  />
                  <style type="text/css">{lazyCss.current?.shadowedCss}</style>
                  <style type="text/css">{editorCss}</style>
                  {pageBorders ? <style type="text/css">{xhtmlCss}</style> : null}
                  {!readOnly ? <style type="text/css">{decorateCss}</style> : null}
                  {showDiff ? <style type="text/css">{diffCss}</style> : null}
                  {withShadowDom ? (
                    <>
                      <link rel="stylesheet" href="/client/static/css/fontawesome.min.css" />
                      <link rel="stylesheet" href="/client/static/css/regular.min.css" />
                      <link rel="stylesheet" href="/client/static/css/solid.min.css" />
                    </>
                  ) : null}
                  {forcePaperFormat && (
                    <style type="text/css">
                      {`
                  .page-content {
                    padding: 0 30px;
                    width: 630px !important;
                    height: 835px !important;
                  }

              `}
                    </style>
                  )}
                </Shadowed>
                {sectionHeader ? <div className="section-name fw-bold">{sectionName}</div> : null}
                {toolbars}
              </CoverageScopeProvider>
            </Slate>
          </EditorContext.Provider>
        </div>
      </EditorSectionProvider>
    </EditorScopeProvider>
  );
};

const decorateCss = `
    .layout-leaf-empty {
    background: rgba(193, 210, 228, 0.23);
      outline: 1px dotted rgba(77, 184, 148, 0.5);
    }
    .layout-leaf:hover,
    .layout-leaf:focus-within {
      outline: 1px dotted rgba(77, 184, 148, 0.5);
    }
    [data-slate-void="true"].reference {
      outline: 2px solid rgba(77, 184, 148, 0.5);
    }
`;

const xhtmlCss = `
    .page-content {
      outline: 2px solid black;
    }
`;

const diffCss = `
  .diffed-deleted {
    background: #fbd8d8;
  }

  .diffed-added {
    background: #e0eff9;
  }

  .diffed-change-added {
    background: #e0eff9;
  }

  .diffed-change-deleted {
    background: #fbd8d8;
  }

  .diffed-change-selected {
    background: #ffd56e;
  }

  .page-wrapper.offset-page .page-content {
    background-color: lightgrey;
  }
`;

interface ShadowedProps {
  withShadowDom: boolean;
  children?: ReactNode;
  className?: string;
}

const Shadowed = forwardRef<HTMLElement, ShadowedProps>(function Shadowed(props, ref) {
  const { withShadowDom, children, ...restProps } = props;
  return withShadowDom ? (
    <root.div {...restProps} ref={ref}>
      {children}
    </root.div>
  ) : (
    <>{children}</>
  );
});

function findPageNode(editor: CustomEditor, nodeId: string | undefined) {
  const nodeGenerator = Node.nodes({ children: editor.children } as Ancestor);

  for (const [node, path] of nodeGenerator) {
    if (SlateElement.isElement(node) && node.uuid === nodeId) {
      const page = editor.children[path[0]];
      return page ?? editor.children[0];
    }
  }
  return editor.children[0];
}

const handleScrollIntoView = (_editor: ReactEditor, domRange: globalThis.Range) => {
  if (!domRange) return;

  const rect = domRange.getBoundingClientRect();
  const padding = 75;
  const bottomThreshold = window.innerHeight - padding;
  const topThreshold = padding;

  if (rect.bottom > bottomThreshold) {
    window.scrollBy({ top: rect.bottom - bottomThreshold, behavior: 'smooth' });
  } else if (rect.top < topThreshold) {
    window.scrollBy({ top: rect.top - topThreshold, behavior: 'smooth' });
  }
};
